import { graphql } from 'gatsby';
import Cookies from 'js-cookie';
import { type ABTestPageConfig } from '../../../netlify/edge-functions/lib/types';
import errorLogger from '../errorLogger';
import getFirstPartyData from './getFirstPartyData';
import {
  CONTROL,
  A_B_TEST_COOKIE_HOST_PATH,
  COOKIE_PREFIX_PAGE_TEST,
} from '../../../netlify/edge-functions/lib/constants';
import {
  getRandomVariant,
  shouldExecuteABTest,
} from '../../../netlify/edge-functions/lib/shared-functions';

type Page = Queries.GeneralPageTemplateQuery['kontentItemGeneralPage'];

export type ABTestResult = {
  elements: NonNullable<Page>['elements'];
  abTestId: string | null;
  abTestVariantId: string | string[] | null;
  abTestComponents: {
    campaignId: string;
    variantId: string;
  }[];
};

/*
 * NOTE A counterpart of this function for serverless exists in: @/netlify/edge-functions/a-b-test.ts
 */
const getAssignedVariantIdFromCookie = (abTestConfig: ABTestPageConfig) => {
  const cookieName = `${COOKIE_PREFIX_PAGE_TEST}${abTestConfig.id}`;
  const cookieVariantId = Cookies.get(cookieName);
  const variants = abTestConfig.variants;
  return variants.find((variant) => cookieVariantId === variant.id)?.id;
};

/*
 * NOTE A counterpart of this function for serverless exists in: @/netlify/edge-functions/a-b-test.ts
 */
const selectNewVariantId = (abTestConfig: ABTestPageConfig) => {
  const abTestId = abTestConfig.id;
  const variants = abTestConfig.variants;
  const newVariant = getRandomVariant(variants);

  if (!newVariant) {
    console.log(
      `selectNewVariantId: There was an error assigning a variant for the A/B Test ${abTestId}.`
    );
    return null;
  }

  const cookieName = `${COOKIE_PREFIX_PAGE_TEST}${abTestId}`;
  // NOTE Save the assigned variant in a cookie for client-side to access it
  Cookies.set(cookieName, newVariant.id, {
    path: A_B_TEST_COOKIE_HOST_PATH,
    expires: new Date(abTestConfig.endDate),
  });

  return newVariant.id;
};

const getABTest = ({
  page,
  processedABTestConfig,
}: {
  page: Page;
  processedABTestConfig: ABTestPageConfig[];
}): ABTestResult => {
  const controlElements = page!.elements;

  // NOTE Result initialization
  const result: ABTestResult = {
    elements: controlElements,
    abTestId: null,
    abTestVariantId: null,
    abTestComponents: [],
  };

  if (typeof window === 'undefined') {
    return result;
  }

  const { a_b_test_snippet__a_b_test } = controlElements;

  // No AB test configured
  if (!a_b_test_snippet__a_b_test?.value) {
    return result;
  }

  const abTest = a_b_test_snippet__a_b_test.value.elements;
  const abTestId = abTest.name.value;
  const abTestConfig = processedABTestConfig.find((test) => test.id === abTestId);
  const isABTestDisabled = !abTestConfig;

  const cookieName = `${COOKIE_PREFIX_PAGE_TEST}${abTestId}`;

  // NOTE Default to control elements if the AB test is disabled (e.g. before the start date or after the end date)
  if (isABTestDisabled) {
    Cookies.remove(cookieName);
    return result;
  }

  const abTestVariants = abTest.variants.value;
  const assignedVariantId = getAssignedVariantIdFromCookie(abTestConfig);
  const firstPartyData = getFirstPartyData();

  const shouldExecute = shouldExecuteABTest({
    personalizations: abTestConfig.personalizations,
    logicalOperator: abTestConfig.logicalOperator,
    firstPartyData,
    isReturningVisitor: Boolean(assignedVariantId),
  });

  // The A/B Test has personalisation and criteria are not met (bypassed by returning visitor)
  if (!shouldExecute) {
    // Return default response
    return result;
  }

  const chosenVariantId = assignedVariantId || selectNewVariantId(abTestConfig);

  // NOTE Something went wrong in the variant selection process
  if (!chosenVariantId) {
    errorLogger(
      new Error(`getABTest: Could not get a random variant for the page with abTestId: ${abTestId}`)
    );
    return result;
  }

  result.abTestId = abTestId;
  result.abTestVariantId = chosenVariantId;

  // NOTE Get elements from the page variant
  if (chosenVariantId !== CONTROL) {
    const selectedVariant = abTestVariants.find(
      (variant) => variant.elements.name.value === chosenVariantId
    );
    const contentVariant = selectedVariant?.elements.content_variant.value;
    const hasElements = contentVariant && 'elements' in contentVariant;

    if (hasElements) {
      result.elements = contentVariant.elements as ABTestResult['elements'];
    } else {
      errorLogger(
        new Error(
          `getABTest: Could not get a variant's elements for abTestId: ${abTestId}, variant chosenVariantId: ${chosenVariantId}`
        )
      );
      Cookies.remove(cookieName);
      result.abTestVariantId = null;
    }
  }

  return result;
};

export const fragment = graphql`
  fragment A_B_TEST on kontent_snippet_a_b_test_snippet {
    a_b_test_snippet__a_b_test {
      value {
        elements {
          name {
            value
          }
          variants {
            value {
              elements {
                name {
                  value
                }
                weight {
                  value
                }
                content_variant {
                  value {
                    ...GENERAL_PAGE_TEMPLATE
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
export default getABTest;
